import { useContext, useEffect, useMemo } from "react";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { AuthUser, FetchUserAttributesOutput } from "aws-amplify/auth";

import { GetCurrentUserQuery } from "@api/graphql";

import { ContextProviderProps, createContext, generateContextProvider } from "./context";
import { useGetAuthContext } from "./useGetAuthContext";

export type AuthenticationContext = AuthUser & {
  userDetails: FetchUserAttributesOutput;
  userGroups: string[];
  currentUserData: GetCurrentUserQuery["currentUserData"];
};

interface AuthenticationContextProviderProps extends ContextProviderProps {}

const authenticationReactContext = createContext<AuthenticationContext>();

export const useAuthenticationContext = () => {
  const { authStatus, signOut } = useAuthenticator((context) => [context.user]);
  const [context, setContext] = useContext(authenticationReactContext);
  const isAuthenticated = authStatus === "authenticated";
  const { authContext, loading, error } = useGetAuthContext(isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && !loading && !error && authContext) {
      setContext(authContext);
    }
  }, [isAuthenticated, authContext, loading, error, setContext]);

  return useMemo(
    () => ({
      auth: context,
      signOut: () => {
        signOut();
        setContext(undefined);
      },
    }),
    [context, setContext, signOut],
  );
};

export const AuthenticationContextProvider = generateContextProvider<
  AuthenticationContext,
  AuthenticationContextProviderProps
>(authenticationReactContext, async () => undefined);
